import { GameState } from "../game/state";
import { Palette } from "../data/palette";
import { TAU } from "../core/math";
import { SPRITES, drawSprite, enemySprite, pixelFor } from "./sprites";
import { MANUAL_RANGE } from "../game/systems/weapons";
import { HUD_BAR } from "../ui/hud";

// Canvas2D renderer with an 80s-arcade / Tron presentation: neon vector shapes
// drawn with additive glow over a scrolling grid floor, finished with CRT
// scanlines and a vignette. World space is rendered under a camera transform;
// screen-space UI is drawn separately (see ui/hud.ts).
export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  dpr = 1;
  width = 0; // CSS pixels
  height = 0;
  private t = 0; // wall-clock-ish animation time

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("2D canvas unsupported");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
    window.addEventListener("orientationchange", () => this.resize());
    // iOS Safari resizes the visible area (not window) as its toolbars move.
    window.visualViewport?.addEventListener("resize", () => this.resize());
    window.visualViewport?.addEventListener("scroll", () => this.resize());
  }

  resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Size the drawing buffer to the canvas's ACTUAL on-screen box so the image
    // is never scaled (which caused the squished look + offset touch on iOS).
    const rect = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, Math.round(rect.width));
    this.height = Math.max(1, Math.round(rect.height));
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
  }

  get viewW(): number {
    return this.width;
  }
  get viewH(): number {
    return this.height;
  }

  render(s: GameState, _alpha: number, dt: number): void {
    this.t += dt;
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    // Clear the whole window.
    ctx.fillStyle = Palette.bg;
    ctx.fillRect(0, 0, this.width, this.height);

    // The board fills the window above a bottom HUD bar. The per-run arena is
    // sized to the window aspect, so it fills edge-to-edge with no letterbox.
    const availH = this.height - HUD_BAR;
    const scale = Math.min(this.width / s.arenaW, availH / s.arenaH);
    let offX = (this.width - s.arenaW * scale) / 2;
    let offY = (availH - s.arenaH * scale) / 2;
    if (s.shake > 0) {
      offX += Math.sin(this.t * 91) * s.shake;
      offY += Math.cos(this.t * 73) * s.shake;
    }

    ctx.save();
    ctx.setTransform(this.dpr * scale, 0, 0, this.dpr * scale, this.dpr * offX, this.dpr * offY);

    this.drawGrid(s.arenaW, s.arenaH);
    this.drawArenaBorder(s.arenaW, s.arenaH);
    this.drawNodes(s);
    this.drawPickups(s);
    this.drawBeams(s);
    this.drawProjectiles(s);
    this.drawEnemies(s);
    this.drawParticles(s);
    this.drawPlayer(s);
    this.drawFloats(s);

    ctx.restore();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.drawScanlines();
    // Accessibility: soften the red hit-flash when "reduce flashing" is on.
    this.drawDamageFlash(s.reduceFlashing ? s.damageFlash * 0.25 : s.damageFlash);
    this.drawVignette();
  }

  private drawDamageFlash(a: number): void {
    if (a <= 0) return;
    const ctx = this.ctx;
    const g = ctx.createRadialGradient(
      this.width / 2,
      this.height / 2,
      Math.min(this.width, this.height) * 0.3,
      this.width / 2,
      this.height / 2,
      Math.max(this.width, this.height) * 0.72,
    );
    g.addColorStop(0, "rgba(218,72,72,0)");
    g.addColorStop(1, `rgba(218,72,72,${0.55 * a})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.width, this.height);
  }

  // ── background ──────────────────────────────────────────────────────────
  private drawGrid(aw: number, ah: number): void {
    const ctx = this.ctx;
    const cell = 50;

    ctx.lineWidth = 1;
    const pulse = 0.5 + 0.5 * Math.sin(this.t * 1.5);
    ctx.strokeStyle = Palette.bgGrid;
    ctx.beginPath();
    for (let x = 0; x <= aw; x += cell) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ah);
    }
    for (let y = 0; y <= ah; y += cell) {
      ctx.moveTo(0, y);
      ctx.lineTo(aw, y);
    }
    ctx.stroke();

    // Brighter accent lines every 4 cells, gently pulsing.
    ctx.strokeStyle = Palette.bgGridBright;
    ctx.globalAlpha = 0.4 + pulse * 0.3;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const big = cell * 4;
    for (let x = 0; x <= aw; x += big) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ah);
    }
    for (let y = 0; y <= ah; y += big) {
      ctx.moveTo(0, y);
      ctx.lineTo(aw, y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private drawArenaBorder(aw: number, ah: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.shadowBlur = 24;
    ctx.shadowColor = Palette.pcktTrail;
    ctx.strokeStyle = Palette.pcktTrail;
    ctx.lineWidth = 3;
    ctx.strokeRect(0, 0, aw, ah);
    ctx.restore();
  }

  // ── entities ────────────────────────────────────────────────────────────
  private drawNodes(s: GameState): void {
    const ctx = this.ctx;
    for (const n of s.nodes) {
      const col = n.online ? Palette.node : Palette.nodeDead;
      ctx.save();
      ctx.shadowBlur = n.online ? 24 : 4;
      ctx.shadowColor = col;
      ctx.strokeStyle = col;
      ctx.lineWidth = 3;

      // Diamond hub
      ctx.beginPath();
      const r = n.radius;
      ctx.moveTo(n.x, n.y - r);
      ctx.lineTo(n.x + r, n.y);
      ctx.lineTo(n.x, n.y + r);
      ctx.lineTo(n.x - r, n.y);
      ctx.closePath();
      ctx.stroke();

      if (n.online) {
        // Inner pulse + packet stream sparks orbiting the node.
        ctx.fillStyle = col;
        ctx.globalAlpha = 0.18 + 0.12 * Math.sin(this.t * 3);
        ctx.fill();
        ctx.globalAlpha = 1;
        const sparks = 6;
        for (let i = 0; i < sparks; i++) {
          const a = this.t * 2 + (i / sparks) * TAU;
          const rr = r + 12 + Math.sin(this.t * 4 + i) * 4;
          ctx.beginPath();
          ctx.arc(n.x + Math.cos(a) * rr, n.y + Math.sin(a) * rr, 2.2, 0, TAU);
          ctx.fill();
        }
        // Stability ring
        const frac = n.stability / n.maxStability;
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 22, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
        ctx.stroke();
        ctx.globalAlpha = 1;
      } else {
        // Offline node: dim red cue so the player can find it to restore.
        ctx.globalAlpha = 0.25 + 0.15 * Math.sin(this.t * 2);
        ctx.strokeStyle = Palette.danger;
        ctx.shadowColor = Palette.danger;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 8, 0, TAU);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Red pulse while actively under attack.
      if (n.underAttack > 0) {
        const pulse = 0.4 + 0.4 * Math.sin(this.t * 18);
        ctx.shadowBlur = 18;
        ctx.shadowColor = Palette.danger;
        ctx.strokeStyle = Palette.danger;
        ctx.globalAlpha = pulse;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 14, 0, TAU);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }
  }

  private drawPickups(s: GameState): void {
    const ctx = this.ctx;
    for (const pk of s.pickups) {
      if (!pk.alive) continue;
      const col = pk.kind === "bandwidth" ? Palette.bandwidth : Palette.fragment;
      ctx.save();
      // Expiring XP orbs fade and blink over their final ~1.2s.
      if (pk.life !== undefined && pk.life < 1.2) {
        const blink = pk.life < 0.6 ? (Math.floor(this.t * 14) % 2 === 0 ? 0.35 : 0.85) : 1;
        ctx.globalAlpha = Math.max(0.2, pk.life / 1.2) * blink;
      }
      ctx.shadowBlur = 12;
      ctx.shadowColor = col;
      ctx.fillStyle = col;
      const r = pk.kind === "bandwidth" ? 3.5 : 4.5;
      ctx.beginPath();
      ctx.moveTo(pk.x, pk.y - r);
      ctx.lineTo(pk.x + r, pk.y);
      ctx.lineTo(pk.x, pk.y + r);
      ctx.lineTo(pk.x - r, pk.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  private drawBeams(s: GameState): void {
    const ctx = this.ctx;
    for (const b of s.beams) {
      if (b.life <= 0) continue;
      const a = Math.max(0, b.life / b.maxLife);
      ctx.save();
      ctx.globalAlpha = a;
      ctx.shadowBlur = 16;
      ctx.shadowColor = b.color;
      ctx.strokeStyle = b.color;
      ctx.lineWidth = b.width;
      ctx.beginPath();
      ctx.moveTo(b.x1, b.y1);
      ctx.lineTo(b.x2, b.y2);
      ctx.stroke();
      // Dark-hot core (reads on the white construct)
      ctx.globalAlpha = a * 0.85;
      ctx.strokeStyle = "#e4e4e4";
      ctx.lineWidth = Math.max(1, b.width * 0.4);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawProjectiles(s: GameState): void {
    const ctx = this.ctx;
    for (const pr of s.projectiles) {
      if (!pr.alive) continue;
      ctx.save();
      ctx.shadowBlur = 12;
      ctx.shadowColor = pr.color;
      ctx.fillStyle = pr.color;
      // Stretch the bolt along its velocity.
      const ang = Math.atan2(pr.vy, pr.vx);
      ctx.translate(pr.x, pr.y);
      ctx.rotate(ang);
      ctx.beginPath();
      ctx.ellipse(0, 0, pr.radius * 1.8, pr.radius * 0.7, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = "#e4e4e4";
      ctx.beginPath();
      ctx.arc(0, 0, pr.radius * 0.35, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
  }

  private drawEnemies(s: GameState): void {
    const ctx = this.ctx;
    for (const e of s.enemies) {
      if (!e.alive) continue;
      if (e.bossState) {
        this.drawBoss(e);
        continue;
      }
      const col = e.def.color;

      // Soft neon glow blob behind the crisp pixel sprite.
      ctx.save();
      ctx.globalAlpha = e.buffed > 0 ? 0.4 : 0.22;
      ctx.shadowBlur = e.buffed > 0 ? 16 : 10;
      ctx.shadowColor = col;
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.radius * 0.85, 0, TAU);
      ctx.fill();
      ctx.restore();

      // 8-bit sprite (white when hit-flashing).
      const sprite = enemySprite(e.def.id, e.def.behavior);
      const px = pixelFor(e.radius, sprite.h, 2);
      drawSprite(ctx, sprite, e.x, e.y, px, e.hitFlash > 0);

      // Damaged enemies show a thin HP arc.
      if (e.hp < e.maxHp) {
        ctx.save();
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = col;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 6;
        ctx.shadowColor = col;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.radius + 6, -Math.PI / 2, -Math.PI / 2 + (e.hp / e.maxHp) * TAU);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  private drawBoss(e: import("../game/entities").Enemy): void {
    const ctx = this.ctx;
    const col = e.hitFlash > 0 ? "#e4e4e4" : e.def.color;
    const r = e.radius;
    ctx.save();
    ctx.translate(e.x, e.y);
    ctx.shadowBlur = 30;
    ctx.shadowColor = e.def.color;

    // Rotating hexagon shell.
    ctx.strokeStyle = col;
    ctx.lineWidth = 4;
    ctx.save();
    ctx.rotate(this.t * 0.5);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      const px = Math.cos(a) * r;
      const py = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    // Pulsing core.
    ctx.fillStyle = e.def.color;
    ctx.globalAlpha = 0.28 + 0.16 * Math.sin(this.t * 4);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.55, 0, TAU);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#e4e4e4";
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.22, 0, TAU);
    ctx.fill();
    ctx.restore();
  }

  private drawParticles(s: GameState): void {
    const ctx = this.ctx;
    for (const p of s.particles) {
      if (p.life <= 0) continue;
      const a = Math.max(0, p.life / p.maxLife);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.globalAlpha = 1;
  }

  private drawPlayer(s: GameState): void {
    const ctx = this.ctx;
    const p = s.player;
    const blink = p.invuln > 0 && Math.floor(this.t * 20) % 2 === 0;

    ctx.save();
    ctx.translate(p.x, p.y);

    // Manual-fire reach — a faint dashed ring so you can see your shot range.
    ctx.globalAlpha = 0.12;
    ctx.strokeStyle = Palette.pckt;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([7, 9]);
    ctx.beginPath();
    ctx.arc(0, 0, MANUAL_RANGE, 0, TAU);
    ctx.stroke();
    ctx.setLineDash([]);

    // Always-on locator ring so PCKT stays easy to find in the chaos.
    const lr = p.radius * 2.4 + Math.sin(this.t * 4) * 3;
    ctx.globalAlpha = 0.16 + 0.07 * Math.sin(this.t * 4);
    ctx.strokeStyle = Palette.pckt;
    ctx.lineWidth = 2;
    ctx.shadowBlur = 8;
    ctx.shadowColor = Palette.pckt;
    ctx.beginPath();
    ctx.arc(0, 0, lr, 0, TAU);
    ctx.stroke();
    ctx.restore();

    // Energy field flowing around PCKT — rotating arcs + orbiting sparks.
    ctx.save();
    ctx.translate(p.x, p.y);
    const fieldR = p.radius * 1.45;
    ctx.shadowBlur = 10;
    ctx.shadowColor = Palette.bandwidth;
    ctx.strokeStyle = Palette.bandwidth;
    ctx.lineWidth = 2;
    for (let i = 0; i < 2; i++) {
      const a0 = this.t * (i === 0 ? 3 : -2.2) + i * Math.PI;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.arc(0, 0, fieldR + i * 5, a0, a0 + Math.PI * 0.6);
      ctx.stroke();
    }
    ctx.shadowBlur = 12;
    for (let i = 0; i < 5; i++) {
      const a = this.t * 2.4 + (i / 5) * TAU;
      const rr = fieldR + Math.sin(this.t * 5 + i) * 5;
      ctx.globalAlpha = 0.55 + 0.35 * Math.sin(this.t * 6 + i);
      ctx.fillStyle = i % 2 === 0 ? Palette.pckt : Palette.node;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * rr, Math.sin(a) * rr, 2.4, 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Neon glow blob behind the pixel sprite.
    ctx.save();
    ctx.globalAlpha = blink ? 0.2 : 0.38;
    ctx.shadowBlur = 16;
    ctx.shadowColor = Palette.pckt;
    ctx.fillStyle = Palette.pckt;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius * 0.9, 0, TAU);
    ctx.fill();
    ctx.restore();

    // 8-bit PCKT sprite.
    ctx.save();
    ctx.globalAlpha = blink ? 0.5 : 1;
    const px = pixelFor(p.radius * 1.05, SPRITES.pckt.h, 3);
    drawSprite(ctx, SPRITES.pckt, p.x, p.y, px);
    ctx.restore();
  }

  private drawFloats(s: GameState): void {
    const ctx = this.ctx;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const f of s.floats) {
      if (f.life <= 0) continue;
      ctx.globalAlpha = Math.min(1, f.life * 2);
      ctx.fillStyle = f.color;
      ctx.font = `bold ${f.size}px "Chakra Petch", "Courier New", monospace`;
      ctx.shadowBlur = 6;
      ctx.shadowColor = f.color;
      ctx.fillText(f.text, f.x, f.y);
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
  }

  // ── post FX ─────────────────────────────────────────────────────────────
  private drawScanlines(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = "#000000";
    for (let y = 0; y < this.height; y += 3) {
      ctx.fillRect(0, y, this.width, 1);
    }
    ctx.restore();
  }

  private drawVignette(): void {
    const ctx = this.ctx;
    const g = ctx.createRadialGradient(
      this.width / 2,
      this.height / 2,
      Math.min(this.width, this.height) * 0.35,
      this.width / 2,
      this.height / 2,
      Math.max(this.width, this.height) * 0.75,
    );
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, "rgba(0,0,0,0.5)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, this.width, this.height);
  }
}
